import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { FileText, Plus, Send, CheckCircle2, Clock, DollarSign, X, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ client_name: '', items: [{ description: '', quantity: 1, rate: 0 }], due_date: '', notes: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => { fetchInvoices(); }, []);
  const fetchInvoices = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API_URL}/api/admin/invoices`, { withCredentials: true }); setInvoices(data); }
    catch { toast.error('Failed to load invoices'); }
    setLoading(false);
  };

  const addItem = () => setForm(p => ({ ...p, items: [...p.items, { description: '', quantity: 1, rate: 0 }] }));
  const updateItem = (i, f, v) => setForm(p => ({ ...p, items: p.items.map((item, idx) => idx === i ? { ...item, [f]: v } : item) }));
  const removeItem = (i) => setForm(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
  const subtotal = form.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  const tax = subtotal * 0.2;
  const total = subtotal + tax;

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/invoices`, { ...form, subtotal, tax, total }, { withCredentials: true });
      toast.success('Invoice created');
      setShowCreate(false);
      setForm({ client_name: '', items: [{ description: '', quantity: 1, rate: 0 }], due_date: '', notes: '' });
      fetchInvoices();
    } catch { toast.error('Failed to create invoice'); }
    setCreating(false);
  };

  const updateStatus = async (num, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/invoices/${num}/status`, { status }, { withCredentials: true });
      toast.success(`Invoice ${status}`);
      fetchInvoices();
    } catch { toast.error('Failed to update'); }
  };

  const statusColors = { draft: 'bg-muted text-muted-foreground', sent: 'bg-[#4A90E2]/15 text-[#4A90E2]', paid: 'bg-[#A8E6CF]/15 text-[#A8E6CF]', overdue: 'bg-red-500/15 text-red-400' };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-invoices-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Invoices</h1>
            <p className="text-muted-foreground mt-1">{invoices.length} invoice(s)</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2" data-testid="create-invoice-btn"><Plus className="w-4 h-4" />Create Invoice</Button>
            </DialogTrigger>
            <DialogContent className="bg-background border-border max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-xl font-heading text-foreground">New Invoice</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 py-2">
                <div className="space-y-2"><Label className="text-foreground">Client Name</Label><Input value={form.client_name} onChange={(e) => setForm(p => ({ ...p, client_name: e.target.value }))} required className="bg-muted border-border text-foreground" /></div>
                <div className="space-y-2"><Label className="text-foreground">Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm(p => ({ ...p, due_date: e.target.value }))} required className="bg-muted border-border text-foreground" /></div>
                <div className="space-y-2">
                  <Label className="text-foreground">Line Items</Label>
                  {form.items.map((item, i) => (
                    <div key={i} className="flex gap-2 items-end">
                      <Input value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} placeholder="Description" className="flex-1 bg-muted border-border text-foreground" />
                      <Input type="number" value={item.quantity} onChange={(e) => updateItem(i, 'quantity', +e.target.value)} className="w-16 bg-muted border-border text-foreground" min="1" />
                      <Input type="number" value={item.rate} onChange={(e) => updateItem(i, 'rate', +e.target.value)} className="w-24 bg-muted border-border text-foreground" step="0.01" min="0" placeholder="Rate" />
                      {form.items.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)} className="text-red-400"><X className="w-4 h-4" /></Button>}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addItem} className="border-border text-foreground gap-1"><Plus className="w-3 h-3" />Add Item</Button>
                </div>
                <div className="border-t border-border pt-3 space-y-1 text-sm">
                  <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-muted-foreground"><span>VAT (20%)</span><span>${tax.toFixed(2)}</span></div>
                  <div className="flex justify-between text-foreground font-bold"><span>Total</span><span>${total.toFixed(2)}</span></div>
                </div>
                <div className="space-y-2"><Label className="text-foreground">Notes</Label><Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="bg-muted border-border text-foreground" placeholder="Optional notes" /></div>
                <Button type="submit" disabled={creating} className="w-full btn-primary">{creating ? 'Creating...' : 'Create Invoice'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-16 glass-card rounded-xl border border-border">
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground text-lg">No invoices yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invoices.map((inv, i) => (
              <Card key={i} className="glass-card border-border" data-testid={`invoice-${inv.invoice_number}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-muted"><DollarSign className="w-5 h-5 text-[#FF6F61]" /></div>
                    <div>
                      <p className="text-foreground font-medium">{inv.invoice_number}</p>
                      <p className="text-sm text-muted-foreground">{inv.client_name} | Due: {inv.due_date || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-foreground font-bold">${inv.total?.toFixed(2)}</span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[inv.status] || statusColors.draft}`}>{inv.status}</span>
                    {inv.status === 'draft' && <Button size="sm" variant="outline" onClick={() => updateStatus(inv.invoice_number, 'sent')} className="border-border text-foreground gap-1"><Send className="w-3 h-3" />Send</Button>}
                    {inv.status === 'sent' && <Button size="sm" variant="outline" onClick={() => updateStatus(inv.invoice_number, 'paid')} className="border-border text-[#A8E6CF] gap-1"><CheckCircle2 className="w-3 h-3" />Mark Paid</Button>}
                    {inv.id && (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => window.open(`${API_URL}/api/finance/invoices/${inv.id}/pdf`, '_blank')}
                        className="border-border text-muted-foreground gap-1"
                        data-testid={`download-pdf-${inv.invoice_number}`}
                      >
                        <Download className="w-3 h-3" />PDF
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
